import { ICell, ICellMetric, ICellMetricUOM, ICubeSource, ICubeSourceCellOperation } from "./PA.HyperRectangle.IF";
import * as _ from "lodash";
import { IMessageProvider, IProgressProvider, MessageType } from "../PA.Core/PA.Core.IF.Basic";
import { Index } from "../PA.Core/Basic/Index";
import { ErrorMessage_restate, WarningMessage_restate } from "../PA.Core/Basic/MessageUtils";
import { UOM } from "../PA.MD.Services/PA.MD.Services.IF";

export interface ICellOperationOptions {
    progressProvider?: IProgressProvider;
    messageProvider?: IMessageProvider;
}

export class CubeSourceUtils {
    static async executeOperation(
        cubeSource: ICubeSource,
        cellOp: ICubeSourceCellOperation,
        opts?: ICellOperationOptions,
    ): Promise<boolean> {
        //
        if (_.isEmpty(cubeSource)) return false;
        const progressProvider = opts?.progressProvider;
        const messageProvider = opts?.messageProvider;
        let tgtCellCount = 0;
        let tgtCellMiss = 0;
        let tgtMetricSetCount = 0;

        let opName = "Copy";
        if (cellOp.Operation == "MetricOperation") {
            switch (cellOp.SubOperation) {
                case "SetTo":
                    opName = "Setting Value";
                    break;
                case "ScaleBy":
                    opName = "Scale Value";
                    break;
            }
        }

        if (progressProvider) progressProvider.start(opName);

        try {
            // Add Metrics to Perspective Options
            const allMetrics = _.clone(cellOp.CubePerspectiveOptions.MetricIds || []);
            const dctMetrics = _.keyBy(allMetrics, (a) => a);
            let arrMetricIds: string[];
            if (cellOp.Operation == "MetricOperation") arrMetricIds = [...cellOp.TargetMetrics];
            else if (cellOp.Operation == "Metric") arrMetricIds = [cellOp.SourceMetric, ...cellOp.TargetMetrics];
            else {
                arrMetricIds = cellOp.CopyMetrics || [];

                // Add Dimension Filter for Source and Target Nodes
                let filterByNodes = [cellOp.SourceNode, ...(cellOp.TargetNodes || [])];
                cellOp.CubePerspectiveOptions.DimensionFilters[cellOp.Operation] = { FilterByNodes: filterByNodes };
            }
            _.forEach(arrMetricIds, (aMetricId) => {
                if (!(aMetricId in dctMetrics)) {
                    allMetrics.push(aMetricId);
                    dctMetrics[aMetricId] = aMetricId;
                }
            });
            cellOp.CubePerspectiveOptions.MetricIds = allMetrics;

            const perspective = cubeSource.createPerspective("Operation", cellOp.CubePerspectiveOptions);
            await perspective.OnCubePerspectiveLoadComplete;

            const periodHierarchy = cubeSource.getDimensionHierarchy("Period");
            let periodHierarchyNodes = periodHierarchy.getNodes();
            const periodLevelId = cellOp.CubePerspectiveOptions.Dimensions["Period"].LevelId;
            periodHierarchyNodes = _.filter(periodHierarchyNodes, (a) => a.NodeLevel == periodLevelId);
            periodHierarchyNodes = _.sortBy(periodHierarchyNodes, (a) => a.NodeOrder);

            const periodIds = _.map(periodHierarchyNodes, (a) => a.NodeId);

            let cellPeriodIds = _.flatMap(perspective.Cells, (aCell) => aCell.getKey("Period"));
            cellPeriodIds = _.uniq(cellPeriodIds);

            cellPeriodIds.sort((a, b) => periodIds.indexOf(a) - periodIds.indexOf(b));

            const dctCellPeriodSrc: { [id: string]: string | number } = {};
            _.forEach(perspective.Cells, (aCell) => {
                let cellId = aCell.CellId;
                dctCellPeriodSrc[cellId] = aCell.getCellMetricValue(cellOp.SourceMetric);
            });

            //  If across Metrics, copy across
            if (cellOp.Operation == "Metric") {
                if (cellOp.OffsetPeriods > 0) {
                    let lastValue: string | number = "";
                    _.forEach(perspective.Cells, (aCell) => {
                        tgtCellCount++;
                        const currentPeirodId = aCell.getKey("Period");
                        const periodIndex = periodIds.indexOf(currentPeirodId);
                        const srcPeriodIndex = periodIndex + cellOp.OffsetPeriods;
                        let srcPeriodId = "";

                        if (srcPeriodIndex >= periodIds.length) {
                            srcPeriodId = periodIds[periodIds.length - 1];
                        } else {
                            srcPeriodId = periodIds[srcPeriodIndex];
                        }

                        let productId = aCell.getKey("Product");
                        let srcCellId = `${productId}-|-${srcPeriodId}`;

                        const metricValue = dctCellPeriodSrc[srcCellId];
                        if (_.isNaN(metricValue)) return;

                        lastValue = _.isUndefined(metricValue) ? lastValue : metricValue;

                        _.forEach(cellOp.TargetMetrics || [], (aTargetMetricId) => {
                            if (_.isUndefined(metricValue)) aCell.setCellMetricValue(aTargetMetricId, lastValue);
                            else aCell.setCellMetricValue(aTargetMetricId, metricValue);
                            tgtMetricSetCount++;
                        });
                    });
                } else {
                    _.forEach(perspective.Cells, (aCell) => {
                        tgtCellCount++;
                        const metricValue = aCell.getCellMetricValue(cellOp.SourceMetric);
                        if (_.isNaN(metricValue)) return;

                        _.forEach(cellOp.TargetMetrics || [], (aTargetMetricId) => {
                            aCell.setCellMetricValue(aTargetMetricId, metricValue);
                            tgtMetricSetCount++;
                        });
                    });
                }
            } else if (cellOp.Operation == "MetricOperation") {
                _.forEach(perspective.Cells, (aCell) => {
                    tgtCellCount++;

                    _.forEach(cellOp.TargetMetrics || [], (aTargetMetricId) => {
                        let tgtValue = <number>aCell.getCellMetricValue(aTargetMetricId);

                        if (_.isNaN(tgtValue)) return;

                        switch (cellOp.SubOperation) {
                            case "SetTo":
                                tgtValue = cellOp.OperationParam1;
                                break;
                            case "ScaleBy":
                                tgtValue *= cellOp.OperationParam1;
                                break;
                            default:
                                return;
                        }

                        aCell.setCellMetricValue(aTargetMetricId, tgtValue);
                        tgtMetricSetCount++;
                    });
                });
            } else {
                const srcDimKey = cellOp.SourceNode.NodeId;
                const srcDimType = cellOp.Operation;
                const dctTgtDimKey = _.keyBy(cellOp.TargetNodes, (n) => n.NodeId);

                const copyFromCells: ICell[] = [];
                const copyToCells: ICell[] = [];
                _.forEach(perspective.Cells, (aCell) => {
                    const cellKey = aCell.getKey(srcDimType);
                    if (cellKey == srcDimKey) copyFromCells.push(aCell);
                    else if (cellKey in dctTgtDimKey) {
                        copyToCells.push(aCell);
                    }
                });

                // create index for target cells
                const idxOrder = cubeSource.getDimensions();
                const idxIndexers: ((t: ICell) => string)[] = [];
                _.forEach(idxOrder, (dimType) => {
                    idxIndexers.push((aCell) => aCell.getKey(dimType));
                });
                const idx = new Index<ICell>(idxIndexers);
                _.forEach(copyToCells, (aCell) => {
                    idx.add(aCell);
                });

                _.forEach(copyFromCells, (aSrcCell) => {
                    _.forEach(cellOp.TargetNodes, (aTgtNode) => {
                        const tgtKeys: string[] = [];
                        _.forEach(idxOrder, (aDimType) => {
                            if (aDimType == srcDimType) {
                                tgtKeys.push(aTgtNode.NodeId);
                            } else {
                                // Use the key from the source
                                tgtKeys.push(aSrcCell.getKey(aDimType));
                            }
                        });

                        const tgtCell: ICell = <ICell>idx.get(tgtKeys);

                        if (tgtCell) {
                            tgtCellCount++;
                            _.forEach(cellOp.CopyMetrics, (aMetricId) => {
                                const srcValue = aSrcCell.getCellMetricValue(aMetricId);
                                if (_.isNumber(srcValue)) {
                                    tgtCell.setCellMetricValue(aMetricId, srcValue);
                                    tgtMetricSetCount++;
                                }
                            });
                        } else {
                            //-- No target
                            tgtCellMiss++;
                        }
                    });
                });
            }

            if (messageProvider)
                messageProvider.addMessage({
                    type: MessageType.Information,
                    code: "Stats",
                    title: `${opName} Completed, Updated ${tgtCellCount} cells.`,
                    description: `Updated ${tgtCellCount} cells, ${tgtMetricSetCount} values.`,
                });

            if (progressProvider) progressProvider.finish();

            return true;
        } catch (err) {
            if (messageProvider)
                messageProvider.addMessage({
                    type: MessageType.Information,
                    code: "Failed",
                    title: `${opName} Failed`,
                    description: `Error: ${JSON.stringify(err, null, "\t")}`,
                });

            if (progressProvider) progressProvider.exception(err);

            return false;
        }
    }

    static validateOperation(
        cubeSource: ICubeSource,
        cellOp: ICubeSourceCellOperation,
        opts?: ICellOperationOptions,
    ): boolean {
        let returnValue = true;
        //--
        const bNoCubeSource = _.isEmpty(cubeSource);
        const bNoCellOperation = _.isEmpty(cellOp);
        ErrorMessage_restate(opts.messageProvider, bNoCubeSource, "NoCubeSource", "No CubeSource");
        ErrorMessage_restate(opts.messageProvider, bNoCellOperation, "NoCellOperation", "No Cell Operation");

        if (bNoCubeSource || bNoCellOperation) return false;

        // Remove all Errors, by Id?
        if (opts.messageProvider)
            _.forEach(
                [
                    "NoOperationType",
                    "UnknownCrossDimension",
                    "NoSourceMetric",
                    "NoTargetMetrics",
                    "OverlapTargetNodes",
                    "SourceTargetMetrics",
                    "NoSubOperationType",
                    "NoOperationParameter",
                    "NoSourceNode",
                    "NoTargetNodes",
                    "NoMetricsSelected",
                ],
                (errCd) => {
                    opts.messageProvider.removeMessage(errCd);
                },
            );

        //--
        const arrDims = _.map(cubeSource.getDimensions(), (a) => cubeSource.getDimensionHierarchy(a));
        const dctDims = _.keyBy(arrDims, (a) => a.DimensionType);
        if (cellOp.Operation == "Metric") {
            let bCheckUOM = false;
            let sourceUOM: ICellMetricUOM = undefined;
            if (_.isEmpty(cellOp.SourceMetric)) {
                WarningMessage_restate(opts.messageProvider, true, "NoSourceMetric", `Select Source Metric`);
                returnValue = false;
            } else {
                if (_.isFunction(cubeSource.getCellMetricBaseUOM)) {
                    sourceUOM = cubeSource.getCellMetricBaseUOM(cellOp.SourceMetric);
                    if (!_.isEmpty(sourceUOM)) bCheckUOM = true;
                }
            }

            if (_.isEmpty(cellOp.TargetMetrics)) {
                WarningMessage_restate(opts.messageProvider, true, "NoTargetMetrics", `Select Target Metrics`);
                returnValue = false;
            }
            _.forEach(cellOp.TargetMetrics, (tgtMtrcId) => {
                if (cellOp.SourceMetric == tgtMtrcId) {
                    WarningMessage_restate(
                        opts.messageProvider,
                        true,
                        "SourceTargetMetrics",
                        `Remove Source Metric from Target Metrics`,
                    );
                    returnValue = false;
                }
                // Check UOM compatibility
                if (bCheckUOM) {
                    const tgtUOM = cubeSource.getCellMetricBaseUOM(tgtMtrcId);
                    if (!CubeSourceUtils.isUOMCopyCompatible(sourceUOM, tgtUOM)) {
                        WarningMessage_restate(
                            opts.messageProvider,
                            true,
                            `UOMMismatch${cellOp.SourceMetric}${tgtMtrcId}`,
                            `UOM mismatch for Target Metric ${tgtMtrcId}`,
                        );
                        returnValue = false;
                    }
                }
            });
        } else if (cellOp.Operation == "MetricOperation") {
            if (_.isEmpty(cellOp.SubOperation)) {
                WarningMessage_restate(opts.messageProvider, true, "NoSubOperationType", `Select Operation Type`);
                returnValue = false;
            }
            if (!_.isNumber(cellOp.OperationParam1)) {
                WarningMessage_restate(
                    opts.messageProvider,
                    true,
                    "NoOperationParameter",
                    `Select Operation Parameter`,
                );
                returnValue = false;
            }

            if (_.isEmpty(cellOp.TargetMetrics)) {
                WarningMessage_restate(opts.messageProvider, true, "NoTargetMetrics", `Select Target Metrics`);
                returnValue = false;
            }
        } else {
            if (!(cellOp.Operation in dctDims)) {
                ErrorMessage_restate(
                    opts.messageProvider,
                    true,
                    "UnknownCrossDimension",
                    `Unknown Cross Dimension ${cellOp.Operation}`,
                );
                return false;
            }
            const crossHier = dctDims[cellOp.Operation];

            if (_.isEmpty(cellOp.SourceNode)) {
                WarningMessage_restate(opts.messageProvider, true, "NoSourceNode", `Select Source Node`);
                returnValue = false;
            }

            if (_.isEmpty(cellOp.TargetNodes)) {
                WarningMessage_restate(opts.messageProvider, true, "NoTargetNodes", `Select Target Nodes`);
                returnValue = false;
            }

            if (!(_.isEmpty(cellOp.SourceNode) || _.isEmpty(cellOp.TargetNodes))) {
                const nodeId = cellOp.SourceNode.NodeId;
                const bExists = !_.isEmpty(_.filter(cellOp.TargetNodes, (a) => nodeId == a.NodeId));
                if (bExists) {
                    WarningMessage_restate(
                        opts.messageProvider,
                        true,
                        "OverlapTargetNodes",
                        `Source Node in Target List, ${cellOp.SourceNode.NodeName} (${cellOp.SourceNode.NodeIdUI})`,
                    );
                    returnValue = false;
                }
            }

            if (_.isEmpty(cellOp.CopyMetrics)) {
                WarningMessage_restate(opts.messageProvider, true, "NoMetricsSelected", `Select Metrics to Copy`);
                returnValue = false;
            }
        }

        // Validate the Dimensions
        const pDims = cellOp?.CubePerspectiveOptions?.Dimensions || {};
        for (const dimHier of arrDims) {
            const dimType = dimHier.DimensionType;
            let sDimLevel = "";
            if (pDims && pDims[dimType]) sDimLevel = pDims[dimType].LevelId;

            if (_.isEmpty(sDimLevel)) {
                WarningMessage_restate(
                    opts.messageProvider,
                    true,
                    `NoDimLevel${dimType}`,
                    `Select Level for ${dimType}`,
                );
                returnValue = false;
            }
        }

        return returnValue;
    }

    static isUOMCopyCompatible(a: ICellMetricUOM, b: ICellMetricUOM): boolean {
        if (_.isEmpty(a) && _.isEmpty(b)) return true;

        if (_.isEmpty(a) && !_.isEmpty(b)) return false;

        if (_.isEmpty(b) && !_.isEmpty(a)) return false;

        function uomCompat(x: UOM, y: UOM): boolean {
            if (_.isEmpty(x) && _.isEmpty(y)) return true;

            if (_.isEmpty(x) && !_.isEmpty(y)) return false;

            if (_.isEmpty(y) && !_.isEmpty(x)) return false;

            if (x.bUOM != y.bUOM) return false;

            if (x.bCurrency != y.bCurrency) return false;

            if (x.bUOM && x.UOMCode != y.UOMCode) return false;

            if (x.bCurrency && x.CurrencyCode != y.CurrencyCode) return false;

            return true;
        }

        return uomCompat(a?.NumeratorUOM, b?.NumeratorUOM) && uomCompat(a?.DenominatorUOM, b?.DenominatorUOM);
    }
}
